import { api } from '@/lib/api'
import { tokenStorage } from '@/lib/auth'
import type { CompanySuggestion, GovData } from './types'

export async function searchCompanies(query: string, limit = 10): Promise<CompanySuggestion[]> {
  if (!query || query.length < 2) return []
  const { data } = await api.get<{ suggestions?: CompanySuggestion[]; result?: { suggestions?: CompanySuggestion[] } }>(
    '/smartform/whisper/combined',
    { params: { q: query, limit, dry_run: false } },
  )
  return data.suggestions ?? data.result?.suggestions ?? []
}

export interface VinDecodeResult {
  Status?: number
  Data?: GovData['Data']
  detail?: string
}

export async function fetchVehicleData(vin: string): Promise<VinDecodeResult> {
  const { data } = await api.get<VinDecodeResult>('/dataovozidlech/vehicle-data', {
    params: { vin },
  })
  return data
}

export interface UploadResult {
  id: number | string
  filename: string
  status?: string
  [key: string]: unknown
}

export async function uploadDocument(file: File): Promise<UploadResult> {
  const form = new FormData()
  form.append('file', file)
  const { data } = await api.post<UploadResult>('/document/upload', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

export interface DocumentStatus {
  id: number | string
  status: string
  is_processed?: boolean
  extracted_metadata?: { items?: ExtractedVehicle[]; error?: string } | null
  [key: string]: unknown
}

export interface ExtractedVehicle {
  vin?: string
  vin_number?: string
  registration_plate_number?: string
  car_gov_data?: { Status?: number; Data?: Record<string, unknown> } | null
  [key: string]: unknown
}

export async function getDocumentStatus(id: number | string): Promise<DocumentStatus> {
  const { data } = await api.get<DocumentStatus>(`/document/${id}`)
  return data
}

export async function fetchDocumentBlob(id: number | string): Promise<Blob> {
  const { data } = await api.get(`/document/${id}/file`, { responseType: 'blob' })
  return data as Blob
}

export interface ParseXlsxStandard {
  format: 'standard'
  rows: Record<string, unknown>[]
  columns: string[]
  vinColumnHint?: string
  totalRows: number
}

export interface ParseXlsxProDoma {
  format: 'pro_doma'
  vins: string[]
  totalRows: number
  vinColumnHint?: string
}

export type ParseXlsxResult = ParseXlsxStandard | ParseXlsxProDoma

export async function parseXlsxOnServer(file: File): Promise<ParseXlsxResult> {
  const form = new FormData()
  form.append('file', file)
  const { data } = await api.post<ParseXlsxResult>('/vehicles/parse-xlsx', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

export async function downloadXlsReport(payload: unknown): Promise<{ blob: Blob; filename: string }> {
  const token = tokenStorage.get()
  const res = await fetch('/api/v1/report/xls-report', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload),
  })
  if (!res.ok) {
    let detail = `HTTP ${res.status}`
    try {
      const json = (await res.json()) as { detail?: string }
      if (json.detail) detail = json.detail
    } catch {
      /* noop */
    }
    throw new Error(detail)
  }
  const cd = res.headers.get('Content-Disposition') ?? ''
  let filename = 'report.xlsx'
  const star = /filename\*=UTF-8''([^;\n]+)/i.exec(cd)
  if (star?.[1]) filename = decodeURIComponent(star[1])
  else {
    const basic = /filename="?([^";\n]+)"?/i.exec(cd)
    if (basic?.[1]) filename = basic[1]
  }
  const blob = await res.blob()
  return { blob, filename }
}
